import { useLocation } from "wouter";
import { ChevronsUpDown, Check } from "lucide-react";
import { useProfile, PERSONAS, PersonaId } from "@/lib/profile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function ProfileSwitcher() {
  const { persona, setPersonaId } = useProfile();
  const [, navigate] = useLocation();

  function pick(id: PersonaId) {
    setPersonaId(id);
    navigate(PERSONAS[id].homePath);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-sidebar-accent/40 transition-colors group">
        <div className={cn("w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0", persona.accent)}>
          {persona.initials}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium text-sidebar-foreground truncate">{persona.name}</div>
          <div className="text-[11px] text-sidebar-foreground/60 truncate">{persona.role}</div>
        </div>
        <ChevronsUpDown className="h-4 w-4 text-sidebar-foreground/40 group-hover:text-sidebar-foreground/70 flex-shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-72">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          View platform as
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {Object.values(PERSONAS).map((p) => {
          const active = p.id === persona.id;
          return (
            <DropdownMenuItem
              key={p.id}
              onSelect={() => pick(p.id)}
              className={cn("cursor-pointer flex items-start gap-3 py-2.5", active && "bg-accent")}
            >
              <div className={cn("w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0", p.accent)}>
                {p.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{p.name}</span>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="text-[11px] text-muted-foreground">{p.role}</div>
                <div className="text-[11px] text-muted-foreground/80 mt-0.5">{p.description}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
